import React, { useEffect, useState } from 'react'
import { Card, CardContent, Grid, Typography } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import { BarChart } from '@mui/x-charts/BarChart'
import { AxisConfig, ChartsYAxisProps } from '@mui/x-charts'
import { AxisScaleConfig, MakeOptional } from '@mui/x-charts/internals'
import { truncate } from '~/features/Dispatch/utils/stringsManipulations'
import { convertSecondsToTime } from '~/utils/dateTime'

// eslint-disable-next-line max-lines-per-function
export default function ColumnChartHorizontal({
  data,
  title,
  height = 800,
  width = 0,
  subtitle = '',
  sxFont = {},
  colorBar = 'primary',
  typeTotal = '',
  info = '',
  tooltip = '',
  Ytype = 'number',
  valueFormatter = (value: any, context: { readonly location: string }) => context.location === 'tick' ? truncate(String(value), 18) : value,
  colorBarFixed='',
  heightFit= false,
  isPrinting = false,
  fullHeight = false,
}) {
  const { translate } = useLang()
  const [marginLeft, setMarginLeft] = useState(310)

  const getColor = {
    'primary': '#6395FA',
    'secondary': '#59BD8E',
    'tertiary': '#EDAE49'
  }

  const yFieldTreatament = data.map((element) => {
    return String(element.item)
  })

  const dataTreatament = data.map((element) => {
    return element.value
  })

  // Extrai cores individuais se existirem no array de dados
  const hasCustomColors = data.some((element) => element.color)
  const customColors = hasCustomColors ? data.map((element) => element.color || getColor[colorBar]) : undefined

  const calculateMaxLabelWidth = (labels) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    let maxWidth = 20;
    if (context) {
      context.font = '14px Arial';

      labels.forEach((label) => {
        const stringTruncated = truncate(String(label), 30)
        const width = context.measureText(String(stringTruncated)).width;
        if (width > maxWidth) {
          maxWidth = width;
        }
      });
    }

    return maxWidth;
  };

  useEffect(() => {
    if (data.length > 0) {
      const maxWidth = calculateMaxLabelWidth(yFieldTreatament);
      setMarginLeft(maxWidth + 25);
    }
  }, [data, yFieldTreatament]);

  return (
    <Card variant="outlined" sx={{ height: heightFit ? 'fit-content' : fullHeight ? '100%' : 650, overflowY: 'auto' }}>
      <CardContent sx={{ height: '80%' }}>
        <Typography sx={{ fontWeight: 'bold', ...sxFont }}>
          {translate(title)}
        </Typography>
        {info && (
          <Typography variant="subtitle2" marginBottom={2} marginTop={1}>
            {translate(info)}
          </Typography>
        )}
        {subtitle &&
          <Typography variant='subtitle2' marginBottom={2} marginTop={1} >
            {translate(subtitle)}
            {typeTotal && ` | ${translate('Total of')} ${translate(typeTotal)}: ${data.length}`}
          </Typography>
        }

        {data.length ? (
          <Grid xs={11}>
            <BarChart
              series={
                hasCustomColors
                  ? data.map((item, index) => ({
                      data: data.map((el, i) => (i === index ? item.value : 0)),
                      stack: 'total',
                      color: item.color || getColor[colorBar],
                    }))
                  : [{
                      data: dataTreatament, 
                      valueFormatter: (value, { dataIndex }) => {
                        return tooltip ? `${tooltip} ${data[dataIndex].tooltipInfo}` : `${value}`
                      },
                    }]
              }
              yAxis={[
                {
                  scaleType: 'band',
                  data: yFieldTreatament,
                  categoryGapRatio: 0.5,
                  tickPlacement: 'middle',
                  tickLabelPlacement: 'tick',
                  valueFormatter(value, context) {
                    return Ytype == 'time' ? `${convertSecondsToTime(value)}` : context.location === 'tick' ? truncate(String(value), 30) : value
                  },
                  tickLabelStyle: {
                    fontSize: 14,
                  },
                  ...(customColors ? {} : {
                    colorMap: {
                      type: 'piecewise',
                      thresholds: [0],
                      colors: [getColor[colorBar]],
                    },
                  }),
                } as unknown as MakeOptional<
                  AxisConfig<keyof AxisScaleConfig, number, ChartsYAxisProps>,
                  'id'
                >,
              ]}
              xAxis={[
                {
                  id: 'linearAxis',
                  scaleType: 'linear',
                  tickMinStep: 1,
                  position: 'top',
                  valueFormatter: valueFormatter,
                },
              ]}
              height={isPrinting ? Math.max(200, yFieldTreatament.length * 40) : (yFieldTreatament.length *
                (yFieldTreatament.length == 1
                  ? 150
                  : yFieldTreatament.length >= 5
                    ? 50
                    : 80))}
              skipAnimation
              width={850}
              layout="horizontal"
              grid={{ vertical: true }}
              bottomAxis={yFieldTreatament.length >= 5 ? 'linearAxis' : null}
              topAxis={'linearAxis'}
              margin={{ left: marginLeft, top: 25 }}
              barLabel={(item) => {
                if (hasCustomColors && item.value === 0) return ''
                // eslint-disable-next-line new-cap
                return Intl.NumberFormat('pt-BR').format(Number(item.value))
              }}
              leftAxis={{ labelStyle: { fontSize: 10 } }}
              slotProps={{
                ...(hasCustomColors && { legend: { hidden: true } }),
                barLabel: { x: 25, style: { fontSize: 16 } }
              }}
              sx={{}}
            />
          </Grid>
        ) : (
          <Grid item sx={{ height: '100%' }}>
            <Card
              variant="outlined"
              sx={{
                display: 'flex',
                height: '100%',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
              }}
            >
              <CardContent>
                <Typography>
                  {translate('No data available')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </CardContent>
    </Card>
  )
}
